import { useMemo } from "react";
import { FaroCoreApiTypes, FaroCoreApiUtils } from "@stellar/core-api-client";
import {
  Autocomplete,
  AutoCompleteOption,
  AutoCompleteSelectedOption,
  InputLabel,
} from "@flat-ui";
import { isValidEmail } from "@pages/create-project/create-project-helper";
import { Box } from "@mui-wrapper";

interface Props {
  /** The current project manager */
  projectManager: string;

  /** The subject of the project creation */
  members: FaroCoreApiTypes.AdmGroupMember[];

  /** Function to set the project manager */
  setProjectManager: (value: string) => void;
}

/** Autocomplete component for selecting a project manager from a list of group members. */
export function ProjectManagerAutocomplete({
  projectManager,
  members,
  setProjectManager,
}: Props): React.JSX.Element {
  const options: AutoCompleteOption[] = useMemo(
    () =>
      members.map(({ user }) => ({
        title: FaroCoreApiUtils.Member.isTeam(user)
          ? (user.name ?? "")
          : user.email,
        subtitle: user.identity ?? "",
        value: user.email,
      })),
    [members]
  );

  const selectedOptions: AutoCompleteSelectedOption[] = projectManager
    ? [projectManager]
    : [];

  function handleChange(newOptions: AutoCompleteSelectedOption[]): void {
    const last = newOptions[newOptions.length - 1];
    if (!last) {
      setProjectManager("");
    } else if (typeof last === "string") {
      setProjectManager(last);
    } else {
      setProjectManager(last.value);
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <InputLabel tag="required">Project Manager</InputLabel>
      <Autocomplete
        options={options}
        selectedOptions={selectedOptions}
        handleChange={handleChange}
        isValid={isValidEmail}
        placeHolder="Type an email address or select from the list"
      />
    </Box>
  );
}
